import { colors, IconEmail } from '@newfront-insurance/core-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { forwardRef, type JSX } from 'react';

import { getExtension, isKnownExtension } from '../../../utils/file';

interface Props {
  filename: string;
  colored?: boolean;
  hasBackground?: boolean;
  hasBoxShadow?: boolean;
  fileExtension?: string;
}

export function FileIcon({
  filename,
  colored = false,
  hasBackground = true,
  hasBoxShadow = false,
  fileExtension,
}: Props): JSX.Element {
  const extension = fileExtension ?? getExtension(filename);
  const knownExtension = isKnownExtension(extension);

  if (!knownExtension) {
    return (
      <IconContainer hasBackground={hasBackground} hasBoxShadow={hasBoxShadow}>
        <IconEmail color={colors.steel[400]} size={18} />
      </IconContainer>
    );
  }

  const lowerCaseExtension = extension?.toLowerCase();

  return (
    <IconContainer hasBackground={hasBackground} hasBoxShadow={hasBoxShadow}>
      <div
        className={cn(
          'bg-steel-400 h-4 w-6 rounded-[2px] text-center',
          colored &&
            lowerCaseExtension && {
              'bg-fire-400': lowerCaseExtension === 'pdf',
              'bg-brand-400': lowerCaseExtension === 'doc' || lowerCaseExtension === 'docx',
              'bg-grass-400':
                lowerCaseExtension === 'xls' || lowerCaseExtension === 'xlsm' || lowerCaseExtension === 'xlsx',
            },
        )}
      >
        <span className="block text-center text-[8px] leading-[15px] font-bold text-white uppercase">{extension}</span>
      </div>
    </IconContainer>
  );
}

const IconContainer = forwardRef<
  HTMLDivElement,
  { hasBackground: boolean; hasBoxShadow: boolean } & React.PropsWithChildren
>(({ hasBackground, hasBoxShadow, children }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'bg-brand-100 dark:bg-background relative inline-flex aspect-square h-[42px] items-center justify-center rounded-xs',
        {
          'box-shadow-[0px_1px_4px_rgba(0,0,0,0.25)]': hasBoxShadow,
          'bg-transparent dark:bg-transparent': !hasBackground,
        },
      )}
    >
      {children}
    </div>
  );
});

IconContainer.displayName = 'IconContainer';
